<script setup>
import { ref, watch, defineProps, defineEmits } from "vue";
import Dialog from "primevue/dialog";
import ProgressSpinner from "primevue/progressspinner";
import Message from "primevue/message";
import Card from "primevue/card";
import Divider from "primevue/divider";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import ToggleSwitch from "primevue/toggleswitch";
import ConfirmDialog from "primevue/confirmdialog";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { dashboardAPI } from "../service/api.js";
import Tag from "primevue/tag";

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  vendorId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["update:visible", "update"]);

const vendor = ref(null);
const loading = ref(false);
const error = ref(null);
const isEditing = ref(false);
const saving = ref(false);
const deleting = ref(false);

const approvedEmails = ref([]);
const loadingEmails = ref(false);
const newEmail = ref("");
const addingEmail = ref(false);
const removingEmailId = ref(null);

const editForm = ref({
  name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  zip_code: "",
  phone: "",
  email: "",
  payment_type: "",
  carepay_vendor_id: "",
  company_tax_id: "",
  batch_payment_preference: "",
  vendor_type: "",
  eor_required: true,
});

const paymentTypeEditOptions = [
  { label: "—", value: "" },
  { label: "ACH/EFT", value: "ACH/EFT" },
  { label: "Instant Virtual Card", value: "Instant Virtual Card" },
];
const batchPaymentPreferenceEditOptions = [
  { label: "—", value: "" },
  { label: "Batch Payments", value: "Batch Payments" },
  { label: "Individual Payments", value: "Individual Payments" },
];
const vendorTypeEditOptions = [
  { label: "—", value: "" },
  { label: "PRIMARY", value: "PRIMARY" },
  { label: "SECONDARY", value: "SECONDARY" },
];

const confirm = useConfirm();
const toast = useToast();

const fetchVendorDetails = async () => {
  if (!props.vendorId) return;

  loading.value = true;
  loadingEmails.value = true;
  error.value = null;
  vendor.value = null;
  isEditing.value = false;
  approvedEmails.value = [];

  const [vendorResult, emailsResult] = await Promise.allSettled([
    dashboardAPI.getVendorById(props.vendorId),
    dashboardAPI.listApprovedEmails(props.vendorId),
  ]);

  loading.value = false;
  loadingEmails.value = false;

  if (vendorResult.status === "rejected") {
    const err = vendorResult.reason;
    console.error("Error fetching vendor details:", err);
    error.value =
      err.response?.status === 404 ? "Vendor not found" : "Failed to load vendor details";
    return;
  }

  vendor.value = vendorResult.value.data;
  syncEditFormFromVendor(vendorResult.value.data);

  if (emailsResult.status === "fulfilled") {
    approvedEmails.value = emailsResult.value.data || [];
  } else {
    console.error("Error fetching approved emails:", emailsResult.reason);
  }
};

const fetchApprovedEmails = async () => {
  if (!props.vendorId) return;
  loadingEmails.value = true;
  try {
    const res = await dashboardAPI.listApprovedEmails(props.vendorId);
    approvedEmails.value = res.data || [];
  } catch (err) {
    console.error("Error fetching approved emails:", err);
    toast.add({ severity: "error", summary: "Failed to refresh", detail: "Could not reload the approved email list.", life: 5000 });
  } finally {
    loadingEmails.value = false;
  }
};

const addEmail = async () => {
  const email = newEmail.value.trim().toLowerCase();
  const parts = email.split("@");
  if (parts.length !== 2 || !parts[0] || !parts[1] || !parts[1].includes(".")) {
    toast.add({ severity: "warn", summary: "Invalid email", detail: "Enter a valid email address.", life: 3000 });
    return;
  }
  addingEmail.value = true;
  try {
    await dashboardAPI.addApprovedEmail(props.vendorId, email);
    newEmail.value = "";
    await fetchApprovedEmails();
    toast.add({ severity: "success", summary: "Email added", detail: `${email} can now receive EOR links.`, life: 3000 });
  } catch (err) {
    const detail =
      err.response?.status === 409
        ? "This email is already approved for this vendor."
        : err.response?.data || err.message || "Failed to add email";
    toast.add({ severity: "error", summary: "Add failed", detail, life: 5000 });
  } finally {
    addingEmail.value = false;
  }
};

const removeEmail = async (emailRow) => {
  removingEmailId.value = emailRow.id;
  try {
    await dashboardAPI.removeApprovedEmail(props.vendorId, emailRow.id);
    await fetchApprovedEmails();
    toast.add({ severity: "success", summary: "Email removed", detail: `${emailRow.email} removed.`, life: 3000 });
  } catch (err) {
    const detail = err.response?.data || err.message || "Failed to remove email";
    toast.add({ severity: "error", summary: "Remove failed", detail, life: 5000 });
  } finally {
    removingEmailId.value = null;
  }
};

function syncEditFormFromVendor(v) {
  editForm.value = {
    name: v.name || "",
    address_line1: v.address_line1 || "",
    address_line2: v.address_line2 || "",
    city: v.city || "",
    state: v.state || "",
    zip_code: v.zip_code || "",
    phone: v.phone || "",
    email: v.email || "",
    payment_type: v.payment_type || "",
    carepay_vendor_id: v.carepay_vendor_id ?? "",
    company_tax_id: v.company_tax_id ?? "",
    batch_payment_preference: v.batch_payment_preference || "",
    vendor_type: v.vendor_type || "",
    eor_required: v.eor_required !== false,
  };
}

// Watch for dialog visibility and vendorId changes
watch(
  () => [props.visible, props.vendorId],
  ([newVisible, newVendorId]) => {
    if (newVisible && newVendorId) {
      fetchVendorDetails();
    }
  },
  { immediate: true }
);

const closeDialog = () => {
  emit("update:visible", false);
};

const startEdit = () => {
  if (vendor.value) syncEditFormFromVendor(vendor.value);
  isEditing.value = true;
};

const cancelEdit = () => {
  isEditing.value = false;
};

const saveVendor = async () => {
  if (!props.vendorId || !vendor.value) return;
  saving.value = true;
  error.value = null;
  try {
    const payload = {
      name: editForm.value.name,
      address_line1: editForm.value.address_line1 || null,
      address_line2: editForm.value.address_line2 || null,
      city: editForm.value.city || null,
      state: editForm.value.state || null,
      zip_code: editForm.value.zip_code || null,
      phone: editForm.value.phone || null,
      email: editForm.value.email || null,
      payment_type: editForm.value.payment_type || null,
      carepay_vendor_id: editForm.value.carepay_vendor_id || null,
      company_tax_id: editForm.value.company_tax_id || null,
      batch_payment_preference: editForm.value.batch_payment_preference || null,
      vendor_type: editForm.value.vendor_type || null,
      eor_required: editForm.value.eor_required,
    };
    const response = await dashboardAPI.patchVendor(props.vendorId, payload);
    vendor.value = response.data;
    isEditing.value = false;
    toast.add({
      severity: "success",
      summary: "Vendor Updated",
      detail: "Changes have been saved.",
      life: 3000,
    });
    emit("update");
  } catch (err) {
    const msg = err.response?.data || err.message || "Failed to save vendor";
    error.value = typeof msg === "string" ? msg : msg.error || "Failed to save vendor";
    toast.add({
      severity: "error",
      summary: "Save Failed",
      detail: error.value,
      life: 5000,
    });
  } finally {
    saving.value = false;
  }
};

const handleDelete = () => {
  confirm.require({
    message:
      "Delete this vendor? This will only succeed if the vendor has no associated transactions.",
    header: "Delete Vendor",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    rejectLabel: "Cancel",
    acceptLabel: "Delete",
    acceptClass: "p-button-danger",
    accept: async () => {
      deleting.value = true;
      try {
        await dashboardAPI.deleteVendor(props.vendorId);
        toast.add({
          severity: "success",
          summary: "Vendor Deleted",
          detail: "Vendor has been deleted.",
          life: 3000,
        });
        emit("update");
        closeDialog();
      } catch (err) {
        const msg =
          err.response?.status === 409
            ? err.response?.data?.error || "Cannot delete vendor: vendor has associated transactions."
            : err.response?.data?.error || err.message || "Failed to delete vendor";
        toast.add({
          severity: "error",
          summary: "Delete Failed",
          detail: msg,
          life: 5000,
        });
      } finally {
        deleting.value = false;
      }
    },
  });
};
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :style="{ width: '50rem', maxHeight: '90vh' }"
    @update:visible="closeDialog"
    :pt="{
      header: { class: 'vendor-dialog-header' }
    }"
  >
    <template #header>
      <div class="dialog-header-content">
        <i class="pi pi-building header-icon"></i>
        <div>
          <h2 class="dialog-title">Vendor Details</h2>
          <p class="dialog-subtitle" v-if="vendor">{{ vendor.name }}</p>
        </div>
        <div v-if="vendor && !loading" class="dialog-header-actions">
          <Button
            v-if="!isEditing"
            label="Edit"
            icon="pi pi-pencil"
            severity="secondary"
            size="small"
            @click="startEdit"
          />
          <Button
            v-if="!isEditing"
            label="Delete"
            icon="pi pi-trash"
            severity="danger"
            size="small"
            outlined
            :loading="deleting"
            @click="handleDelete"
          />
          <Button
            v-if="isEditing"
            label="Cancel"
            icon="pi pi-times"
            severity="secondary"
            size="small"
            @click="cancelEdit"
          />
          <Button
            v-if="isEditing"
            label="Save"
            icon="pi pi-check"
            size="small"
            :loading="saving"
            @click="saveVendor"
          />
        </div>
      </div>
    </template>

    <div v-if="loading" class="loading-container">
      <ProgressSpinner />
      <p class="loading-text">Loading vendor details...</p>
    </div>

    <Message v-else-if="error" severity="error" :closable="false">
      {{ error }}
    </Message>

    <div v-else-if="vendor && isEditing" class="vendor-edit-form">
      <Message v-if="error" severity="error" :closable="false" class="mb-2">
        {{ error }}
      </Message>
      <div class="form-grid">
        <div class="field">
          <label for="edit-name">Company Name *</label>
          <InputText id="edit-name" v-model="editForm.name" class="w-full" />
        </div>
        <div class="field">
          <label for="edit-carepay">CarePay Vendor ID</label>
          <InputText id="edit-carepay" v-model="editForm.carepay_vendor_id" class="w-full" />
        </div>
        <div class="field">
          <label for="edit-tax">Tax ID</label>
          <InputText id="edit-tax" v-model="editForm.company_tax_id" class="w-full" />
        </div>
        <div class="field">
          <label for="edit-payment-type">Payment Type</label>
          <Select
            id="edit-payment-type"
            v-model="editForm.payment_type"
            :options="paymentTypeEditOptions"
            option-label="label"
            option-value="value"
            placeholder="—"
            class="w-full"
          />
        </div>
        <div class="field">
          <label for="edit-batch-pref">Batch Payment Preference</label>
          <Select
            id="edit-batch-pref"
            v-model="editForm.batch_payment_preference"
            :options="batchPaymentPreferenceEditOptions"
            option-label="label"
            option-value="value"
            placeholder="—"
            class="w-full"
          />
        </div>
        <div class="field">
          <label for="edit-vendor-type">Vendor Type</label>
          <Select
            id="edit-vendor-type"
            v-model="editForm.vendor_type"
            :options="vendorTypeEditOptions"
            option-label="label"
            option-value="value"
            placeholder="—"
            class="w-full"
          />
        </div>
        <div class="field">
          <label for="edit-eor-required">EOR Required</label>
          <ToggleSwitch id="edit-eor-required" v-model="editForm.eor_required" />
        </div>
        <div class="field full-width">
          <label for="edit-address1">Address Line 1</label>
          <InputText id="edit-address1" v-model="editForm.address_line1" class="w-full" />
        </div>
        <div class="field full-width">
          <label for="edit-address2">Address Line 2</label>
          <InputText id="edit-address2" v-model="editForm.address_line2" class="w-full" />
        </div>
        <div class="field">
          <label for="edit-city">City</label>
          <InputText id="edit-city" v-model="editForm.city" class="w-full" />
        </div>
        <div class="field">
          <label for="edit-state">State</label>
          <InputText id="edit-state" v-model="editForm.state" class="w-full" />
        </div>
        <div class="field">
          <label for="edit-zip">Zip Code</label>
          <InputText id="edit-zip" v-model="editForm.zip_code" class="w-full" />
        </div>
        <div class="field">
          <label for="edit-phone">Phone</label>
          <InputText id="edit-phone" v-model="editForm.phone" class="w-full" />
        </div>
        <div class="field">
          <label for="edit-email">Email</label>
          <InputText id="edit-email" v-model="editForm.email" class="w-full" type="email" />
        </div>
      </div>
    </div>

    <div v-else-if="vendor" class="vendor-details">
      <!-- Basic Information Card -->
      <Card class="info-card info-card-primary">
        <template #header>
          <div class="card-header">
            <i class="pi pi-id-card card-icon"></i>
            <h3 class="card-title">Basic Information</h3>
          </div>
        </template>
        <template #content>
          <div class="card-content">
            <div class="detail-row">
              <div class="detail-item">
                <span class="label">
                  <i class="pi pi-hashtag"></i>
                  Vendor ID
                </span>
                <span class="value">{{ vendor.id }}</span>
              </div>
              <div class="detail-item">
                <span class="label">
                  <i class="pi pi-tag"></i>
                  CarePay Vendor ID
                </span>
                <span class="value">{{ vendor.carepay_vendor_id || "N/A" }}</span>
              </div>
            </div>
            <Divider />
            <div class="detail-row">
              <div class="detail-item">
                <span class="label">
                  <i class="pi pi-briefcase"></i>
                  Company Name
                </span>
                <span class="value value-highlight">{{ vendor.name }}</span>
              </div>
              <div class="detail-item">
                <span class="label">
                  <i class="pi pi-file"></i>
                  Tax ID
                </span>
                <span class="value">{{ vendor.company_tax_id || "N/A" }}</span>
              </div>
            </div>
            <Divider />
            <div class="detail-row">
              <div class="detail-item">
                <span class="label">
                  <i class="pi pi-credit-card"></i>
                  Payment Type
                </span>
                <span class="value">{{ vendor.payment_type || "N/A" }}</span>
              </div>
              <div class="detail-item">
                <span class="label">
                  <i class="pi pi-wallet"></i>
                  Batch Payment Preference
                </span>
                <span class="value">{{ vendor.batch_payment_preference || "N/A" }}</span>
              </div>
            </div>
            <Divider />
            <div class="detail-row">
              <div class="detail-item">
                <span class="label">
                  <i class="pi pi-file-check"></i>
                  EOR Required
                </span>
                <span class="value">{{ vendor.eor_required === false ? "No" : "Yes" }}</span>
              </div>
            </div>
          </div>
        </template>
      </Card>

      <!-- Contact Information Card (includes address) -->
      <Card class="info-card info-card-secondary">
        <template #header>
          <div class="card-header">
            <i class="pi pi-phone card-icon"></i>
            <h3 class="card-title">Contact Information</h3>
          </div>
        </template>
        <template #content>
          <div class="card-content">
            <div class="detail-row">
              <div class="detail-item">
                <span class="label">
                  <i class="pi pi-phone"></i>
                  Phone
                </span>
                <span class="value">{{ vendor.phone || "N/A" }}</span>
              </div>
              <div class="detail-item">
                <span class="label">
                  <i class="pi pi-envelope"></i>
                  Email
                </span>
                <span class="value value-link">{{ vendor.email || "N/A" }}</span>
              </div>
            </div>
            <Divider />
            <div class="address-block">
              <div class="address-line">
                <i class="pi pi-home"></i>
                {{ vendor.address_line1 || "N/A" }}
              </div>
              <div class="address-line" v-if="vendor.address_line2">
                {{ vendor.address_line2 }}
              </div>
              <div class="address-line">
                <i class="pi pi-map"></i>
                {{ vendor.city || "N/A" }}, {{ vendor.state || "N/A" }} {{ vendor.zip_code || "" }}
              </div>
            </div>
          </div>
        </template>
      </Card>
      <!-- Approved EOR Emails Card -->
      <Card class="info-card info-card-eor">
        <template #header>
          <div class="card-header">
            <i class="pi pi-envelope card-icon"></i>
            <h3 class="card-title">Approved EOR Emails</h3>
          </div>
        </template>
        <template #content>
          <div class="card-content">
            <div class="email-add-row">
              <InputText
                v-model="newEmail"
                placeholder="email@example.com"
                class="email-input"
                type="email"
                @keyup.enter="addEmail"
              />
              <Button
                label="Add"
                icon="pi pi-plus"
                size="small"
                :loading="addingEmail"
                @click="addEmail"
              />
            </div>
            <div v-if="loadingEmails" class="email-loading">
              <ProgressSpinner style="width: 1.5rem; height: 1.5rem" />
            </div>
            <div v-else-if="approvedEmails.length === 0" class="email-empty">
              No approved emails — add one above to allow EOR link requests.
            </div>
            <ul v-else class="email-list">
              <li v-for="row in approvedEmails" :key="row.id" class="email-row">
                <Tag :value="row.email" severity="secondary" class="email-tag" />
                <span class="email-meta">added by {{ row.approved_by_user_id }}</span>
                <Button
                  icon="pi pi-times"
                  severity="danger"
                  size="small"
                  text
                  rounded
                  :loading="removingEmailId === row.id"
                  @click="removeEmail(row)"
                  class="email-remove"
                  aria-label="Remove"
                />
              </li>
            </ul>
          </div>
        </template>
      </Card>
    </div>
    <ConfirmDialog />
  </Dialog>
</template>

<style scoped>
/* Dialog Header Styling */
.dialog-header-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}
.dialog-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}
.vendor-edit-form {
  padding: 0;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.form-grid .field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.form-grid .field.full-width {
  grid-column: 1 / -1;
}
.form-grid .field label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--p-text-muted-color);
}
.mb-2 {
  margin-bottom: 1rem;
}

.header-icon {
  font-size: 1.5rem;
  color: var(--p-primary-color);
  background: var(--p-primary-50);
  padding: 0.5rem;
  border-radius: 8px;
}

.dialog-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--p-text-color);
}

.dialog-subtitle {
  margin: 0.15rem 0 0 0;
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
  font-weight: 500;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1.5rem;
}

.loading-text {
  color: var(--p-text-muted-color);
  font-size: 1rem;
  margin: 0;
}

/* Main Container */
.vendor-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0;
}

/* Card Styling */
.info-card {
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 1rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

.card-icon {
  font-size: 1.1rem;
  padding: 0.4rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--p-text-color);
}

/* Primary Card (Basic Info) */
.info-card-primary .card-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.info-card-primary .card-header {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
}

/* Secondary Card (Contact Information) */
.info-card-secondary .card-icon {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
}

.info-card-secondary .card-header {
  background: linear-gradient(135deg, rgba(79, 172, 254, 0.05) 0%, rgba(0, 242, 254, 0.05) 100%);
}

/* Card Content */
.card-content {
  padding: 0.85rem;
}

.detail-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.85rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--p-text-muted-color);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.label i {
  font-size: 0.8rem;
  color: var(--p-primary-color);
}

.value {
  font-size: 0.9rem;
  color: var(--p-text-color);
  font-weight: 500;
  padding: 0.4rem 0.6rem;
  background: var(--p-surface-50);
  border-radius: 5px;
  border-left: 2px solid var(--p-primary-color);
}

.value-highlight {
  font-weight: 600;
  color: var(--p-primary-color);
  background: var(--p-primary-50);
  border-left-color: var(--p-primary-500);
}

.value-link {
  color: var(--p-primary-color);
  cursor: pointer;
}

.value-link:hover {
  text-decoration: underline;
}

/* Address Styling */
.address-block {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--p-surface-50);
  border-radius: 6px;
  border-left: 3px solid #f5576c;
}

.address-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.88rem;
  color: var(--p-text-color);
  line-height: 1.5;
}

.address-line i {
  color: #f5576c;
  font-size: 0.9rem;
  min-width: 1rem;
}

/* Divider Styling */
:deep(.p-divider) {
  margin: 0.65rem 0;
}

/* EOR Emails Card */
.info-card-eor .card-icon {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  color: white;
}

.info-card-eor .card-header {
  background: linear-gradient(135deg, rgba(67, 233, 123, 0.05) 0%, rgba(56, 249, 215, 0.05) 100%);
}

.email-add-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.email-input {
  flex: 1;
}

.email-loading {
  display: flex;
  justify-content: center;
  padding: 0.75rem;
}

.email-empty {
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
  padding: 0.5rem 0;
}

.email-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.email-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.5rem;
  border-radius: 5px;
  background: var(--p-surface-50);
}

.email-tag {
  font-size: 0.82rem;
}

.email-meta {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  flex: 1;
}

.email-remove {
  margin-left: auto;
  flex-shrink: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .detail-row {
    grid-template-columns: 1fr;
  }

  .payment-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
}
</style>
